import type { StaffMemoryFact } from "@lafayette/schemas";
import { type ReactNode, useId } from "react";

import { Button } from "../../ui/button";
import {
  DetailLink,
  FactBadges,
  FactContext,
  FactMeta,
  type FactPosition,
  FactValue,
  nameWith,
} from "./parts";

export type FactAction =
  | "confirm"
  | "dispute"
  | "resolve"
  | "correct"
  | "delete"
  | "activate"
  | "reject"
  | "accept";

const ACTION_LABELS: Readonly<Record<FactAction, string>> = {
  confirm: "Confirm",
  dispute: "Dispute",
  resolve: "Resolve dispute",
  correct: "Correct",
  delete: "Delete",
  activate: "Activate",
  reject: "Reject",
  accept: "Accept",
};

/** What a staff member may do to a fact in its state. A closed fact can only be erased. */
export function actionsFor(fact: StaffMemoryFact): readonly FactAction[] {
  switch (fact.status) {
    case "ACTIVE":
      return ["confirm", "dispute", "correct", "delete"];
    case "DISPUTED":
      return ["resolve", "correct", "delete"];
    case "SUPERSEDED":
    case "EXPIRED":
      return fact.value === null ? [] : ["delete"];
    default:
      return [];
  }
}

/**
 * One button for each action. The name is the static verb, then the kind and the position of the fact (`aria-labelledby` points at the
 * `FactContext` text of the card), so two cards never share a name. The value the button acts on is its description
 * (`aria-describedby`; a screen reader reads it after the name). The value is never in an `aria-label` or a `title`, which the error
 * reporter copies into a click breadcrumb.
 */
function FactActionButtons({
  fact,
  actions,
  onAction,
  describedBy,
  contextId,
}: Readonly<{
  fact: StaffMemoryFact;
  actions: readonly FactAction[];
  onAction: (action: FactAction, fact: StaffMemoryFact) => void;
  describedBy: string;
  contextId: string;
}>) {
  const base = useId();
  return (
    <>
      {actions.map((action) => {
        const ownId = `${base}-${action}`;
        return (
          <Button
            key={action}
            id={ownId}
            variant={action === "delete" || action === "reject" ? "danger" : "soft"}
            size="sm"
            aria-describedby={describedBy}
            aria-labelledby={nameWith(ownId, contextId)}
            onClick={() => onAction(action, fact)}
          >
            {ACTION_LABELS[action]}
          </Button>
        );
      })}
    </>
  );
}

type FactCardProps = Readonly<{
  fact: StaffMemoryFact;
  customerId: string;
  now: Date;
  /** The actions to offer. Empty for a role that cannot manage. */
  actions: readonly FactAction[];
  onAction: (action: FactAction, fact: StaffMemoryFact) => void;
  /** Shows the kind above the value (the review and history lists do not group by kind). */
  showKind?: boolean;
  /** Where the card sits in its list ("active memory", 2, 5): it makes the names of its controls unique. */
  position?: FactPosition;
  children?: ReactNode;
}>;

/** One fact: its value, where it came from, how fresh it is, and the actions the role may take. */
export function FactCard({
  fact,
  customerId,
  now,
  actions,
  onAction,
  showKind = false,
  position,
  children,
}: FactCardProps) {
  const valueId = useId();
  const contextId = useId();
  return (
    <li className="grid min-w-0 gap-3 rounded-md border border-hairline bg-card p-4">
      <FactContext id={contextId} kind={fact.kind} position={position} visible={showKind} />
      <FactValue fact={fact} id={valueId} />
      <FactBadges fact={fact} />
      <FactMeta fact={fact} now={now} />
      {children}
      <div className="flex flex-wrap gap-2 max-sm:[&>*]:flex-1">
        <DetailLink
          customerId={customerId}
          fact={fact}
          describedBy={valueId}
          contextId={contextId}
        />
        <FactActionButtons
          fact={fact}
          actions={actions}
          onAction={onAction}
          describedBy={valueId}
          contextId={contextId}
        />
      </div>
    </li>
  );
}
